import { ZTooltip } from '../ztooltip';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { T } from 'src/shared/helpers';
import styled from 'styled-components';
import { Carousel } from '../../pages/aiStudio/components/presets/carousel';

// ─── Data ────────────────────────────────────────────────────────────────────

const PREDEFINED_PRINTING_METHODS: DiscoveryItem[] = [
    {
        id: 'laser-engraving',
        name: 'Laser Engraving',
        description: 'Limits customization to single-color engraving. Customers can add text and simple graphics with a precise, premium feel.'
    },
    {
        id: 'screenprinting',
        name: 'Screenprinting',
        description: 'Restricts the palette to a fixed set of spot colors. Ideal for bold, vibrant designs with limited color choices.'
    },
    {
        id: 'monochromie',
        name: 'Monochromie',
        description: 'Single-color printing only. Customers design with one ink color, creating a clean and minimal customization experience.'
    },
    {
        id: 'pantone-colors',
        name: 'Pantone Colors',
        description: 'Restricts color selection to Pantone swatches. Ensures color accuracy and consistency across different print runs.'
    },
    {
        id: 'cmyk-colors',
        name: 'CMYK Colors',
        description: 'Full-color process printing. Customers get the widest color range for photographic and complex multicolor designs.'
    },
    {
        id: 'dtg-printing',
        name: 'DTG Printing',
        description: 'Direct-to-garment printing for high-detail, full-color designs directly onto fabric. Perfect for photo-realistic apparel.'
    },
    {
        id: 'sublimation',
        name: 'Sublimation',
        description: 'Dye-sublimation for all-over prints on polyester. Vibrant, edge-to-edge designs that become part of the fabric.'
    },
    {
        id: 'embroidery',
        name: 'Embroidery',
        description: 'Thread-based embroidery with limited stitch colors. Adds a premium, textured feel to logos and text.'
    },
    {
        id: 'uv-printing',
        name: 'UV Printing',
        description: 'UV-cured ink printing on rigid surfaces like wood, metal, and plastic. Supports full-color with raised texture effects.'
    },
    {
        id: 'foil-stamping',
        name: 'Foil Stamping',
        description: 'Metallic foil application for luxury finishes. Limited to single-color metallic or holographic effects.'
    },
    {
        id: 'heat-transfer',
        name: 'Heat Transfer',
        description: 'Heat-pressed vinyl transfers for simple, durable designs. Best for text, numbers, and basic shapes.'
    },
    {
        id: 'pad-printing',
        name: 'Pad Printing',
        description: 'Indirect offset printing for irregular surfaces like mugs and pens. Supports limited spot colors.'
    },
    {
        id: 'debossing',
        name: 'Debossing',
        description: 'Pressed-in impression without ink for an elegant, tactile finish. Ideal for leather goods, notebooks, and packaging.'
    },
    {
        id: 'risograph',
        name: 'Risograph',
        description: 'Stencil-based layered printing with a distinctive grainy, vintage aesthetic. Limited spot color palette per pass.'
    },
    {
        id: 'letterpress',
        name: 'Letterpress',
        description: 'Traditional relief printing with deep impression into thick stock. Single or dual color for a classic, artisanal look.'
    },
    {
        id: 'flexography',
        name: 'Flexography',
        description: 'High-speed rotary printing with flexible plates. Ideal for packaging, labels, and large-volume runs with spot colors.'
    },
    {
        id: 'gravure-printing',
        name: 'Gravure Printing',
        description: 'Engraved cylinder printing for ultra-high volume production. Delivers rich, consistent color on flexible materials.'
    }
];

const PREDEFINED_PRICING_RULES: DiscoveryItem[] = [
    {
        id: 'per-customization',
        name: 'Per Customization',
        description: 'Charge a fixed fee for each customization element added (text, image, clipart). More elements = higher price.'
    },
    {
        id: 'per-print-area',
        name: 'Per Print Area',
        description: 'Price varies based on how many sides or areas the customer customizes. Front only costs less than front + back.'
    },
    { id: 'per-color', name: 'Per Color', description: 'Charge based on the number of ink colors used. Ideal for screenprinting and spot-color workflows.' },
    {
        id: 'quantity-tiers',
        name: 'Quantity Tiers',
        description: 'Offer volume discounts with tiered pricing. Higher quantities unlock lower per-unit customization costs.'
    }
];

const PREDEFINED_AI_TOOLS: DiscoveryItem[] = [
    { id: 'ai-image-generation', name: 'Image Generation', description: 'Let customers generate and edit images with AI directly inside the customizer.' },
    {
        id: 'ai-design-generation',
        name: 'Design Generation',
        description: 'AI-powered design suggestions that customers can apply to their products in one click.'
    },
    {
        id: 'ai-product-staging',
        name: 'Product Staging',
        description: 'Automatically generate lifestyle scenes and backgrounds for product previews using AI.'
    },
    {
        id: 'ai-virtual-try-on',
        name: 'Virtual Try-On',
        description: 'Let customers see how the customized product looks on them using AI-powered virtual try-on.'
    }
];

const PREDEFINED_TOOLS: DiscoveryItem[] = [
    {
        id: 'get-a-quote',
        name: 'Get a Quote',
        description: 'Replaces Add to Cart with a quote request form. Customers fill in their details and submit a quotation request.'
    }
];

type CategoryId = 'printing' | 'pricing' | 'ai' | 'tools';

interface CategoryDef {
    id: CategoryId;
    label: string;
    items: DiscoveryItem[];
    color: string;
    saveLabel: string;
    buildYourOwnTooltip?: string;
    hasBuildYourOwn: boolean;
}

// ─── Styled ──────────────────────────────────────────────────────────────────

const PanelContainer = styled.div`
    flex-shrink: 0;
    padding: 10px 24px;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const TabsRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const CategoryTab = styled.button<{ $active: boolean; $color: string }>`
    padding: 5px 14px;
    border: 1.5px solid ${(props) => (props.$active ? props.$color : 'transparent')};
    border-radius: 6px;
    background: ${(props) => (props.$active ? props.$color + '12' : 'transparent')};
    color: ${(props) => (props.$active ? props.$color : '#64748b')};
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
    white-space: nowrap;

    &:hover {
        color: ${(props) => props.$color};
        background: ${(props) => props.$color + '08'};
    }
`;

const ContentRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const CarouselWrapper = styled.div`
    flex: 1;
    min-width: 0;
`;

const PillButton = styled.button<{ $selected: boolean; $color: string }>`
    flex-shrink: 0;
    display: flex;
    align-items: center;
    padding: 6px 16px;
    border: 1.5px solid ${(props) => (props.$selected ? props.$color : '#d1d5db')};
    border-radius: 20px;
    background: ${(props) => (props.$selected ? props.$color + '14' : 'white')};
    color: ${(props) => (props.$selected ? props.$color : '#374151')};
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;

    &:hover {
        border-color: ${(props) => props.$color};
        background: ${(props) => props.$color + '0a'};
    }
`;

const StickyEnd = styled.div`
    position: sticky;
    right: 0;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    padding-left: 16px;
    background: linear-gradient(to right, transparent 0%, white 12px);
    z-index: 2;
`;

const BuildYourOwnItem = styled.button`
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 6px 16px;
    border: 1.5px dashed #9ca3af;
    border-radius: 20px;
    background: white;
    color: #6b7280;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;

    &:hover {
        border-color: #6b7280;
        color: #374151;
    }
`;

const SaveActionsRow = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
`;

const SaveLink = styled.button<{ $color?: string }>`
    padding: 0;
    border: none;
    background: none;
    color: ${(props) => props.$color ?? '#2196f3'};
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;
    text-decoration: underline;
    transition: color 0.2s ease;

    &:hover {
        opacity: 0.8;
    }
`;

// ─── Types & Props ───────────────────────────────────────────────────────────

export interface DiscoveryItem {
    id: string;
    name: string;
    description?: string;
}

// Keep backward compat alias
export type PrintTypeItem = DiscoveryItem;

interface DiscoveryModePanelProps {
    selections: Record<string, string | null>;
    onSelect: (category: string, id: string | null) => void;
    onSave: (category: string, item: DiscoveryItem) => void;
    onBuildYourOwn: (category: string) => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

export const DiscoveryModePanel: FC<DiscoveryModePanelProps> = ({ selections, onSelect, onSave, onBuildYourOwn }) => {
    const CATEGORIES: CategoryDef[] = useMemo(
        () => [
            {
                id: 'printing',
                label: T._('Printing Methods', 'Admin'),
                items: PREDEFINED_PRINTING_METHODS,
                color: '#2196f3',
                saveLabel: T._('Save "{0}" as my Printing Method', 'Admin'),
                hasBuildYourOwn: true,
                buildYourOwnTooltip: T._('Create a custom printing method from scratch', 'Admin')
            },
            {
                id: 'pricing',
                label: T._('Pricing Rules', 'Admin'),
                items: PREDEFINED_PRICING_RULES,
                color: '#8b5cf6',
                saveLabel: T._('Save "{0}" as my Pricing Rule', 'Admin'),
                hasBuildYourOwn: true,
                buildYourOwnTooltip: T._('Create a custom pricing rule from scratch', 'Admin')
            },
            {
                id: 'ai',
                label: T._('AI Creative Tools', 'Admin'),
                items: PREDEFINED_AI_TOOLS,
                color: '#ec4899',
                saveLabel: T._('Activate "{0}"', 'Admin'),
                hasBuildYourOwn: true,
                buildYourOwnTooltip: T._('Explore all AI Creative Tools', 'Admin')
            },
            {
                id: 'tools',
                label: T._('Tools', 'Admin'),
                items: PREDEFINED_TOOLS,
                color: '#f59e0b',
                saveLabel: T._('Activate "{0}"', 'Admin'),
                hasBuildYourOwn: false
            }
        ],
        []
    );

    const [activeTab, setActiveTab] = useState<CategoryId>('printing');
    const category = CATEGORIES.find((c) => c.id === activeTab)!;
    const selectedId = selections[activeTab] ?? null;
    const selectedItem = category.items.find((item) => item.id === selectedId) ?? null;

    const handleItemClick = useCallback(
        (item: DiscoveryItem) => {
            onSelect(activeTab, selectedId === item.id ? null : item.id);
        },
        [activeTab, selectedId, onSelect]
    );

    return (
        <PanelContainer>
            {/* Category tabs */}
            <TabsRow>
                {CATEGORIES.map((cat) => (
                    <CategoryTab key={cat.id} $active={activeTab === cat.id} $color={cat.color} onClick={() => setActiveTab(cat.id)}>
                        {cat.label}
                        {selections[cat.id] ? ' \u2022' : ''}
                    </CategoryTab>
                ))}
            </TabsRow>

            {/* Items carousel for active tab */}
            <ContentRow>
                <CarouselWrapper>
                    <Carousel variant='default' gap={8} scrollAmount={200}>
                        {category.items.map((item) => (
                            <ZTooltip key={item.id} position='bottom' title={item.description ?? ''} enterDelay={300} exitDelay={0} interactive={false}>
                                <PillButton $selected={selectedId === item.id} $color={category.color} onClick={() => handleItemClick(item)}>
                                    {item.name}
                                </PillButton>
                            </ZTooltip>
                        ))}
                        {category.hasBuildYourOwn && (
                            <StickyEnd>
                                <ZTooltip position='bottom' title={category.buildYourOwnTooltip ?? ''} enterDelay={300} exitDelay={0} interactive={false}>
                                    <BuildYourOwnItem onClick={() => onBuildYourOwn(activeTab)}>{'+ ' + T._('Build your own', 'Admin')}</BuildYourOwnItem>
                                </ZTooltip>
                            </StickyEnd>
                        )}
                    </Carousel>
                </CarouselWrapper>
            </ContentRow>
            {/* Save links for all active selections across categories */}
            {CATEGORIES.some((cat) => selections[cat.id]) && (
                <SaveActionsRow>
                    {CATEGORIES.map((cat) => {
                        const selId = selections[cat.id];
                        if (!selId) return null;
                        const item = cat.items.find((i) => i.id === selId);
                        if (!item) return null;
                        return (
                            <SaveLink key={cat.id} $color={cat.color} onClick={() => onSave(cat.id, item)}>
                                {cat.saveLabel.replace('{0}', item.name)}
                            </SaveLink>
                        );
                    })}
                </SaveActionsRow>
            )}
        </PanelContainer>
    );
};

export default DiscoveryModePanel;
